/**
 * @fileoverview RateCardsSection — the firm's rate cards in timesheet
 * settings (MOR-1038).
 *
 * Standard is always listed first and cannot be edited here: it is the firm's
 * grade rates (the section above), not a card. Every other card is Standard
 * times a multiplier. Cards are archived, never deleted — jobs already on an
 * archived card keep being priced by it.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Archive, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { extractErrorMessage } from "@/utils/errors";
import { Badge, Button } from "@/ui/primitives";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import {
  RateCardFormDialog,
  type EditableRateCard,
} from "./RateCardFormDialog";
import { describeMultiplier, describeRounding } from "./rateCards.helpers";
import { GROUP_CARD_CLASS } from "./settingsStyles";

function jobCountLabel(count: number): string {
  if (count === 0) return "No jobs";
  return count === 1 ? "1 job" : `${count} jobs`;
}

export function RateCardsSection({
  orgId,
  canEdit,
  today,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
  /** ISO date, passed in so the grades query stays cacheable. */
  today: string;
}) {
  const { data: cards } = useQuery(
    convexQuery(api.timesheets.rateCards.listByOrg, { orgId }),
  );
  const { data: grades } = useQuery(
    convexQuery(api.timesheets.grades.listByOrg, { orgId, today }),
  );
  const archiveCard = useMutation(api.timesheets.rateCards.archive);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EditableRateCard | null>(null);
  const [archiving, setArchiving] = useState<EditableRateCard | null>(null);

  const handleArchive = async () => {
    if (!archiving) return;
    try {
      await archiveCard({ rateCardId: archiving._id });
      toast.success("Rate card archived.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not archive the rate card."));
    } finally {
      setArchiving(null);
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="flex items-start justify-between gap-4 border-b border-border/50 p-6">
        <div>
          <h2 className="text-base font-semibold text-primary">Rate Cards</h2>
          <p className="mt-1 text-sm text-primary/60">
            Charge a job at a multiple of your standard rates, for example a
            discount on bank work or an uplift on contingency work. Put a job on
            a card from the job&apos;s Timesheets tab.
          </p>
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 shrink-0"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="size-4" />
            New rate card
          </Button>
        )}
      </div>

      <div className="flex flex-col divide-y divide-border/50">
        <div className="flex flex-wrap items-center gap-3 px-6 py-3">
          <span className="min-w-52 text-sm font-medium">Standard</span>
          <span className="text-sm text-muted-foreground">
            Your grade rates, as set above
          </span>
          <Badge className="ml-auto bg-muted text-muted-foreground">
            Default for every job
          </Badge>
        </div>

        {(cards ?? []).map((card) => (
          <div
            key={card._id}
            className="flex flex-wrap items-center gap-3 px-6 py-3"
          >
            <span className="min-w-52 text-sm font-medium">{card.name}</span>
            <span className="text-sm tabular-nums text-muted-foreground">
              {card.multiplier}x · {describeMultiplier(card.multiplier)} ·{" "}
              {describeRounding(card.roundTo)}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              {jobCountLabel(card.jobCount)}
            </span>
            {canEdit && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => {
                    setEditing(card);
                    setFormOpen(true);
                  }}
                  aria-label={`Edit ${card.name}`}
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-muted-foreground hover:text-destructive"
                  onClick={() => setArchiving(card)}
                  aria-label={`Archive ${card.name}`}
                >
                  <Archive className="size-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <RateCardFormDialog
        open={formOpen}
        orgId={orgId}
        card={editing}
        grades={grades ?? []}
        onOpenChange={setFormOpen}
      />

      <AlertDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this rate card?</AlertDialogTitle>
            <AlertDialogDescription>
              “{archiving?.name}” will no longer be offered for new jobs.
              {archiving && archiving.jobCount > 0
                ? ` The ${jobCountLabel(archiving.jobCount).toLowerCase()} already on it ${archiving.jobCount === 1 ? "stays" : "stay"} on it and ${archiving.jobCount === 1 ? "keeps" : "keep"} being charged at its rates until you move ${archiving.jobCount === 1 ? "it" : "them"}.`
                : " No jobs are on it."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleArchive();
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
